/**
 * Loads whatever lives at a record address, for whoever is asking.
 *
 * With a `?t=` token → the token read function (scope-checked, identifiers
 * stripped server-side; signed-in callers only since migration 724 — a reader
 * with no session gets `sign_in_required`). Without one → the member read under RLS, composed
 * into the same shape so the paper renders identically for both readers.
 */

import { useEffect, useState } from 'react';
import { getPorSnapshot, listPorSnapshots } from '../../lib/planOfRecordService';
import { getWorkspace } from '../../lib/workspaceService';
import {
  readRecordByShareToken,
  type ShareReadError,
  type SharedRecordRead,
} from '../../lib/porShareService';
import type { AssembledPor, PorCommitment } from '../../types/plan-of-record';

export type RecordAccess = 'member' | 'token';

export interface RecordAddressState {
  status: 'loading' | 'ready' | 'error';
  access: RecordAccess;
  data: SharedRecordRead | null;
  /** Token errors are typed; member errors are messages. */
  error: ShareReadError | string | null;
}

export function useRecordAddress(params: {
  porId: string;
  version: number | null;
  commitmentId: string | null;
  token: string | null;
}): RecordAddressState {
  const { porId, version, commitmentId, token } = params;
  const [state, setState] = useState<RecordAddressState>({
    status: 'loading',
    access: token ? 'token' : 'member',
    data: null,
    error: null,
  });

  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading', access: token ? 'token' : 'member', data: null, error: null });

    const run = async () => {
      if (token) {
        const { data, error } = await readRecordByShareToken(token, { version, commitmentId });
        if (cancelled) return;
        if (error || !data) {
          setState({ status: 'error', access: 'token', data: null, error: error ?? 'unavailable' });
          return;
        }
        setState({ status: 'ready', access: 'token', data, error: null });
        return;
      }

      // Member path: RLS decides. Compose the same shape the token read returns.
      const [snap, list] = await Promise.all([
        getPorSnapshot(porId, version ?? undefined),
        listPorSnapshots(porId),
      ]);
      if (cancelled) return;
      if (snap.error || !snap.data) {
        setState({
          status: 'error',
          access: 'member',
          data: null,
          error: snap.error ?? (version != null ? 'No ratified record with that version.' : 'This Plan of Record has not been ratified yet.'),
        });
        return;
      }
      const payload = snap.data.payload as AssembledPor;
      const workspaceId = payload?.record?.workspace_id;
      const ws = workspaceId ? await getWorkspace(workspaceId).catch(() => null) : null;
      if (cancelled) return;

      let commitment: PorCommitment | null = null;
      if (commitmentId) {
        commitment = (payload?.commitments ?? []).find((c) => c.id === commitmentId) ?? null;
        if (!commitment) {
          setState({ status: 'error', access: 'member', data: null, error: 'That commitment is not part of this version of the record.' });
          return;
        }
      }

      setState({
        status: 'ready',
        access: 'member',
        data: {
          scope: 'record',
          record: {
            id: porId,
            title: payload?.record?.title ?? 'Plan of Record',
            workspaceName: (ws as { name?: string } | null)?.name ?? null,
          },
          snapshot: {
            version: snap.data.version,
            authorizedAt: snap.data.authorized_at,
            ratificationAuthority: snap.data.ratification_authority,
            rationale: snap.data.rationale,
            superseded: snap.data.superseded_by_snapshot_id !== null,
            hasCycleReport: snap.data.source_cycle_report_id !== null,
            hasPlanVersion: snap.data.source_plan_version_id !== null,
          },
          versions: (list.data ?? []).map((v) => ({
            version: v.version,
            authorizedAt: v.authorized_at,
            ratificationAuthority: v.ratification_authority,
            superseded: v.superseded_by_snapshot_id !== null,
          })),
          payload,
          commitment,
          linkedSignalEvents: commitment
            ? (payload?.signal_events ?? []).filter((s) => s.linked_commitment_id === commitment!.id)
            : [],
        },
        error: null,
      });
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [porId, version, commitmentId, token]);

  return state;
}
